import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, MapPin, Navigation, Loader2, X, LocateFixed, Move, Check, Plus, Minus, Crosshair } from "lucide-react";
import axios from "axios";
import LocationPermissionBanner from "./LocationPermissionBanner";

const API = process.env.REACT_APP_BACKEND_URL;

// Cache for map settings (Google Maps API key availability)
let cachedMapSettings = null;
let mapSettingsFetchPromise = null;

// Fetch map settings (with caching)
const getMapSettings = async () => {
  if (cachedMapSettings) return cachedMapSettings;
  
  if (mapSettingsFetchPromise) return mapSettingsFetchPromise;
  
  mapSettingsFetchPromise = (async () => {
    try {
      const response = await axios.get(`${API}/api/settings/map-settings/public`);
      cachedMapSettings = response.data;
      return cachedMapSettings;
    } catch (error) {
      console.error("Failed to fetch map settings:", error);
      return { map_provider: "free", google_maps_configured: false };
    }
  })();
  
  return mapSettingsFetchPromise;
};

// Google Maps search via backend proxy
const searchLocationsGoogle = async (query) => {
  if (!query || query.length < 3) return [];
  
  try {
    const response = await axios.get(`${API}/api/geocode/search`, {
      params: { query, country: "in" }
    });
    return response.data.results || [];
  } catch (error) {
    console.error("Google location search error:", error);
    return [];
  }
};

// Google Maps reverse geocoding via backend proxy
const reverseGeocodeGoogle = async (lat, lng) => {
  try {
    const response = await axios.get(`${API}/api/geocode/reverse`, {
      params: { lat, lng }
    });
    return response.data;
  } catch (error) {
    console.error("Google reverse geocode error:", error);
    return null;
  }
};

// Nominatim API for geocoding (OpenStreetMap) - Fallback
const searchLocationsNominatim = async (query, countryCode = "in") => {
  if (!query || query.length < 3) return [];
  
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?` +
      `format=json&q=${encodeURIComponent(query)}&countrycodes=${countryCode}&limit=5&addressdetails=1`,
      {
        headers: {
          "Accept-Language": "en",
        },
      }
    );
    
    if (!response.ok) throw new Error("Search failed");
    
    const data = await response.json();
    return data.map((item) => ({
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
      address: item.display_name,
      shortAddress: formatShortAddress(item),
    }));
  } catch (error) {
    console.error("Nominatim location search error:", error);
    return [];
  }
};

// Reverse geocoding - Nominatim fallback
const reverseGeocodeNominatim = async (lat, lng) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
      {
        headers: {
          "Accept-Language": "en",
        },
      }
    );
    
    if (!response.ok) throw new Error("Reverse geocode failed");
    
    const data = await response.json();
    return {
      lat,
      lng,
      address: data.display_name,
      shortAddress: formatShortAddress(data),
    };
  } catch (error) {
    console.error("Nominatim reverse geocode error:", error);
    return { lat, lng, address: `${lat.toFixed(4)}, ${lng.toFixed(4)}`, shortAddress: "Selected Location" };
  }
};

// Smart search - uses Google if configured, otherwise Nominatim
const searchLocations = async (query, countryCode = "in") => {
  const settings = await getMapSettings();
  
  if (settings.google_maps_configured) {
    const googleResults = await searchLocationsGoogle(query);
    if (googleResults.length > 0) return googleResults;
  }
  
  // Fallback to Nominatim
  return searchLocationsNominatim(query, countryCode);
};

// Smart reverse geocode - uses Google if configured, otherwise Nominatim
const reverseGeocode = async (lat, lng) => {
  const settings = await getMapSettings();
  
  if (settings.google_maps_configured) {
    const googleResult = await reverseGeocodeGoogle(lat, lng);
    if (googleResult && googleResult.address) return googleResult;
  }
  
  // Fallback to Nominatim
  return reverseGeocodeNominatim(lat, lng);
};

// Format address to shorter version
const formatShortAddress = (item) => {
  const address = item.address || {};
  const parts = [];
  
  if (address.amenity || address.building || address.shop) {
    parts.push(address.amenity || address.building || address.shop);
  }
  if (address.road) parts.push(address.road);
  if (address.suburb || address.neighbourhood) {
    parts.push(address.suburb || address.neighbourhood);
  }
  if (address.city || address.town || address.village) {
    parts.push(address.city || address.town || address.village);
  }
  
  return parts.slice(0, 3).join(", ") || item.display_name?.split(",").slice(0, 2).join(", ") || "Unknown";
};

// Calculate distance using Haversine formula
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Map movement handler with debounced geocoding - DRAG ONLY
const MapMoveHandler = ({ onMapMove, onMoveStart, onMoveEnd, debounceMs = 1500 }) => {
  const map = useMap();
  const moveTimeoutRef = useRef(null);
  const isDraggingRef = useRef(false);

  useMapEvents({
    dragstart: () => {
      isDraggingRef.current = true;
      onMoveStart?.();
      if (moveTimeoutRef.current) clearTimeout(moveTimeoutRef.current);
    },
    dragend: () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        if (moveTimeoutRef.current) clearTimeout(moveTimeoutRef.current);
        
        moveTimeoutRef.current = setTimeout(() => {
          onMoveEnd?.();
          const center = map.getCenter();
          onMapMove(center.lat, center.lng);
        }, debounceMs);
      }
    },
  });

  useEffect(() => {
    return () => {
      if (moveTimeoutRef.current) clearTimeout(moveTimeoutRef.current);
    };
  }, []);

  return null;
};

// Component to pan map to location - clears after panning
const PanToLocation = ({ location, zoom = 16, onPanComplete }) => {
  const map = useMap();
  const hasPannedRef = useRef(false);
  
  useEffect(() => {
    if (location && !hasPannedRef.current) {
      hasPannedRef.current = true;
      map.setView([location.lat, location.lng], zoom, { animate: true });
      // Clear the pan request after completing
      setTimeout(() => {
        onPanComplete?.();
      }, 500);
    }
  }, [location?.lat, location?.lng, zoom, map, onPanComplete]);
  
  // Reset when location changes
  useEffect(() => {
    hasPannedRef.current = false;
  }, [location?.lat, location?.lng]);
  
  return null;
};

// Fit bounds to show both markers
const FitBoundsToMarkers = ({ pickup, drop }) => {
  const map = useMap();
  
  useEffect(() => {
    if (pickup && drop) {
      const bounds = L.latLngBounds(
        [pickup.lat, pickup.lng],
        [drop.lat, drop.lng]
      );
      map.fitBounds(bounds, { padding: [80, 80], maxZoom: 15 });
    }
  }, [pickup?.lat, pickup?.lng, drop?.lat, drop?.lng, map]);
  
  return null;
};

// Map Controls Component - Zoom In/Out and Current Location
const MapControls = ({ onCurrentLocation, isLoadingLocation }) => {
  const map = useMap();
  
  const handleZoomIn = () => {
    map.zoomIn();
  };
  
  const handleZoomOut = () => {
    map.zoomOut();
  };
  
  return (
    <div className="absolute right-3 top-3 z-[1000] flex flex-col gap-2">
      {/* Zoom Controls */}
      <div className="bg-card rounded-lg shadow-lg border border-border overflow-hidden">
        <button
          onClick={handleZoomIn}
          className="w-10 h-10 flex items-center justify-center hover:bg-muted transition-colors border-b border-border"
          data-testid="map-zoom-in"
          title="Zoom in"
        >
          <Plus className="w-5 h-5 text-foreground" />
        </button>
        <button
          onClick={handleZoomOut}
          className="w-10 h-10 flex items-center justify-center hover:bg-muted transition-colors"
          data-testid="map-zoom-out"
          title="Zoom out"
        >
          <Minus className="w-5 h-5 text-foreground" />
        </button>
      </div>
      
      {/* Current Location Button */}
      <button
        onClick={onCurrentLocation}
        disabled={isLoadingLocation}
        className="w-10 h-10 bg-card rounded-lg shadow-lg border border-border flex items-center justify-center hover:bg-muted transition-colors disabled:opacity-50"
        data-testid="map-current-location"
        title="Go to current location"
      >
        {isLoadingLocation ? (
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
        ) : (
          <Crosshair className="w-5 h-5 text-primary" />
        )}
      </button>
    </div>
  );
};

// Component to set map ref
const MapRefSetter = ({ mapRef }) => {
  const map = useMap();
  
  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);
  
  return null;
};

// Create custom marker icons
const createMarkerIcon = (color, label) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background:${color};width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:3px solid white;box-shadow:0 2px 10px rgba(0,0,0,0.3)"><span style="color:white;font-weight:bold;font-size:12px">${label}</span></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
  });
};

// Sharp Center Pin Component with pulsing animation
const CenterPin = ({ isPickup, isMoving, isLoading }) => {
  const color = isPickup ? "#22C55E" : "#EF4444";
  const label = isPickup ? "P" : "D";
  
  return (
    <div 
      className="absolute inset-0 flex items-center justify-center pointer-events-none"
      style={{ zIndex: 1000 }}
    >
      {/* Pin Shadow */}
      <div 
        className={`absolute w-6 h-3 bg-black/30 rounded-full transition-all duration-200 ${isMoving ? 'scale-75 opacity-50' : ''}`}
        style={{ transform: `translateY(${isMoving ? '30px' : '28px'})` }}
      />
      
      {/* Pin Container */}
      <div 
        className={`relative transition-transform duration-200 ${isMoving ? '-translate-y-3' : ''}`}
        style={{ transform: `translateY(-24px) ${isMoving ? 'translateY(-12px)' : ''}` }}
      >
        {/* Pulse ring animation when moving */}
        {isMoving && (
          <div 
            className="absolute inset-0 animate-ping"
            style={{
              width: '48px',
              height: '48px',
              marginLeft: '-4px',
              marginTop: '-4px',
              borderRadius: '50%',
              backgroundColor: `${color}30`,
            }}
          />
        )}
        
        {/* Pin Head */}
        <div 
          className={`w-10 h-10 rounded-full flex items-center justify-center shadow-xl transition-all duration-200 ${isMoving ? 'scale-110' : ''}`}
          style={{ 
            backgroundColor: color,
            border: "3px solid white",
            boxShadow: `0 4px 20px ${color}60`
          }}
        >
          <span className="text-white font-bold text-sm">{label}</span>
        </div>
        
        {/* Pin Needle */}
        <div 
          className="absolute left-1/2 -translate-x-1/2"
          style={{
            top: "36px",
            width: 0,
            height: 0,
            borderLeft: "10px solid transparent",
            borderRight: "10px solid transparent",
            borderTop: `20px solid ${color}`,
            filter: "drop-shadow(0 3px 3px rgba(0,0,0,0.3))"
          }}
        />
        
        {/* Point dot at tip */}
        <div 
          className="absolute left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-white shadow-sm"
          style={{ top: "52px" }}
        />
      </div>
      
      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute" style={{ top: "calc(50% + 50px)" }}>
          <div className="flex items-center gap-2 bg-card px-3 py-1.5 rounded-full shadow-lg">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            <span className="text-xs text-muted-foreground">Finding address...</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Route line between pickup and drop
const RouteLine = ({ pickup, drop }) => {
  if (!pickup || !drop) return null;
  
  const positions = [
    [pickup.lat, pickup.lng],
    [drop.lat, drop.lng]
  ];
  
  return (
    <Polyline 
      positions={positions}
      pathOptions={{
        color: '#3B82F6',
        weight: 4,
        opacity: 0.8,
        dashArray: '10, 10',
        lineCap: 'round'
      }}
    />
  );
};

// Main Location Picker Component
const LocationPicker = ({ 
  pickup, 
  drop, 
  onPickupChange, 
  onDropChange,
  onConfirm,
  onSelectionModeChange // Callback when selection mode changes
}) => {
  const [pickupSearch, setPickupSearch] = useState("");
  const [dropSearch, setDropSearch] = useState("");
  const [pickupResults, setPickupResults] = useState([]);
  const [dropResults, setDropResults] = useState([]);
  const [searchingPickup, setSearchingPickup] = useState(false);
  const [searchingDrop, setSearchingDrop] = useState(false);
  const [selectingMode, setSelectingMode] = useState(null); // null, 'pickup', 'drop'
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [tempLocation, setTempLocation] = useState(null);
  const [isMapMoving, setIsMapMoving] = useState(false);
  const [pickupInputFocused, setPickupInputFocused] = useState(false);
  const [dropInputFocused, setDropInputFocused] = useState(false);
  const [panToLocation, setPanToLocation] = useState(null);
  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);
  
  const pickupTimeoutRef = useRef(null);
  const dropTimeoutRef = useRef(null);
  const selectionMapCenterRef = useRef(null); // Stores center when selection mode starts
  const selectionMapRef = useRef(null); // Ref to access the selection map instance
  const previewMapRef = useRef(null); // Ref to access the preview map instance
  
  const defaultCenter = [28.6139, 77.2090]; // Delhi

  // Search pickup locations
  useEffect(() => {
    if (pickupTimeoutRef.current) clearTimeout(pickupTimeoutRef.current);
    
    if (pickupSearch.length >= 3 && pickupInputFocused) {
      setSearchingPickup(true);
      pickupTimeoutRef.current = setTimeout(async () => {
        const results = await searchLocations(pickupSearch);
        setPickupResults(results);
        setSearchingPickup(false);
      }, 400);
    } else {
      setPickupResults([]);
      setSearchingPickup(false);
    }
    
    return () => {
      if (pickupTimeoutRef.current) clearTimeout(pickupTimeoutRef.current);
    };
  }, [pickupSearch, pickupInputFocused]);

  // Search drop locations
  useEffect(() => {
    if (dropTimeoutRef.current) clearTimeout(dropTimeoutRef.current);
    
    if (dropSearch.length >= 3 && dropInputFocused) {
      setSearchingDrop(true);
      dropTimeoutRef.current = setTimeout(async () => {
        const results = await searchLocations(dropSearch);
        setDropResults(results);
        setSearchingDrop(false);
      }, 400);
    } else {
      setDropResults([]);
      setSearchingDrop(false);
    }
    
    return () => {
      if (dropTimeoutRef.current) clearTimeout(dropTimeoutRef.current);
    };
  }, [dropSearch, dropInputFocused]);

  // Notify parent when selection mode changes
  useEffect(() => {
    if (onSelectionModeChange) {
      onSelectionModeChange(selectingMode);
    }
  }, [selectingMode, onSelectionModeChange]);

  const handleMapMove = async (lat, lng) => {
    setGeocoding(true);
    const location = await reverseGeocode(lat, lng);
    setTempLocation(location);
    setGeocoding(false);
  };

  const confirmTempLocation = () => {
    if (tempLocation) {
      if (selectingMode === 'pickup') {
        onPickupChange(tempLocation);
      } else if (selectingMode === 'drop') {
        onDropChange(tempLocation);
      }
      setTempLocation(null);
      setSelectingMode(null);
      setIsMapMoving(false);
    }
  };

  const startSelectingPickup = () => {
    // Store the initial center for the map BEFORE changing state
    const center = pickup 
      ? [pickup.lat, pickup.lng] 
      : defaultCenter;
    selectionMapCenterRef.current = center;
    
    setSelectingMode('pickup');
    setPickupResults([]);
    setDropResults([]);
    
    // If we have an existing pickup, set it as tempLocation
    // Otherwise, geocode the default center to set initial tempLocation
    if (pickup) {
      setTempLocation(pickup);
    } else {
      // Immediately geocode the center so the confirm button is enabled
      handleMapMove(center[0], center[1]);
    }
  };

  const startSelectingDrop = () => {
    // Store the initial center for the map BEFORE changing state
    const center = drop 
      ? [drop.lat, drop.lng] 
      : pickup 
        ? [pickup.lat, pickup.lng] 
        : defaultCenter;
    selectionMapCenterRef.current = center;
    
    setSelectingMode('drop');
    setPickupResults([]);
    setDropResults([]);
    
    // If we have an existing drop, set it as tempLocation
    // Otherwise, geocode the center to set initial tempLocation
    if (drop) {
      setTempLocation(drop);
    } else {
      // Immediately geocode the center so the confirm button is enabled
      handleMapMove(center[0], center[1]);
    }
  };

  const getCurrentLocation = (type) => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }
    
    setLoadingLocation(true);
    setLocationPermissionDenied(false); // Reset before trying
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const location = await reverseGeocode(latitude, longitude);
        if (type === 'pickup') {
          onPickupChange(location);
          setPanToLocation(location);
        } else {
          onDropChange(location);
          setPanToLocation(location);
        }
        setLoadingLocation(false);
      },
      (error) => {
        console.error("Geolocation error:", error);
        setLoadingLocation(false);
        if (error.code === error.PERMISSION_DENIED) {
          setLocationPermissionDenied(true);
        } else {
          // For other errors (timeout, position unavailable), show alert
          alert("Unable to get your location. Please search manually.");
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Go to current location in selection map mode
  const goToCurrentLocationOnMap = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }
    
    setLoadingLocation(true);
    setLocationPermissionDenied(false); // Reset before trying
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        // Pan the map to current location
        if (selectionMapRef.current) {
          selectionMapRef.current.setView([latitude, longitude], 16, { animate: true });
        }
        // After map settles, trigger reverse geocoding
        setTimeout(async () => {
          const location = await reverseGeocode(latitude, longitude);
          setTempLocation(location);
          setLoadingLocation(false);
        }, 500);
      },
      (error) => {
        console.error("Geolocation error:", error);
        setLoadingLocation(false);
        if (error.code === error.PERMISSION_DENIED) {
          setLocationPermissionDenied(true);
        } else {
          alert("Unable to get your location. Please search manually.");
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Go to current location on the preview map
  const goToCurrentLocationOnPreviewMap = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }
    
    setLoadingLocation(true);
    setLocationPermissionDenied(false); // Reset before trying
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        if (previewMapRef.current) {
          previewMapRef.current.setView([latitude, longitude], 15, { animate: true });
        }
        setLoadingLocation(false);
      },
      (error) => {
        console.error("Geolocation error:", error);
        setLoadingLocation(false);
        if (error.code === error.PERMISSION_DENIED) {
          setLocationPermissionDenied(true);
        } else {
          alert("Unable to get your location. Please search manually.");
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const selectPickupResult = (result) => {
    onPickupChange(result);
    setPickupSearch("");
    setPickupResults([]);
    setPickupInputFocused(false);
    // Navigate map to selected location
    setPanToLocation(result);
  };

  const selectDropResult = (result) => {
    onDropChange(result);
    setDropSearch("");
    setDropResults([]);
    setDropInputFocused(false);
    // Navigate map to selected location
    setPanToLocation(result);
  };

  const distance = pickup && drop ? calculateDistance(pickup.lat, pickup.lng, drop.lat, drop.lng) : null;

  // Map selection mode - single point at a time
  if (selectingMode) {
    // Use the stored center from ref (set when selection mode started) - this prevents re-renders
    const stableCenter = selectionMapCenterRef.current || defaultCenter;
    
    return (
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="p-4 bg-card border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-semibold text-foreground">
                Select {selectingMode === 'pickup' ? 'Pickup' : 'Drop-off'} Location
              </h3>
              <p className="text-sm text-muted-foreground">Drag the map to position the pin</p>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => { 
                setSelectingMode(null); 
                setTempLocation(null); 
                setIsMapMoving(false);
                selectionMapCenterRef.current = null;
              }}
              data-testid="close-map-selection"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Selected location display - stable, no flickering */}
          <div className={`p-3 rounded-lg border-2 transition-colors ${
            selectingMode === 'pickup' 
              ? 'border-green-500 bg-green-50 dark:bg-green-950/30' 
              : 'border-red-500 bg-red-50 dark:bg-red-950/30'
          }`}>
            <div className="flex items-center gap-2 min-h-[24px]">
              <MapPin className={`w-4 h-4 flex-shrink-0 ${selectingMode === 'pickup' ? 'text-green-600' : 'text-red-600'}`} />
              <span className="text-sm font-medium line-clamp-2">
                {isMapMoving 
                  ? "Move map to select location..." 
                  : geocoding 
                    ? "Finding address..." 
                    : (tempLocation?.shortAddress || tempLocation?.address || "Drag map to select")}
              </span>
            </div>
          </div>
        </div>

        {/* Map - using stable center that doesn't change on state updates */}
        <div className="flex-1 relative" style={{ minHeight: "350px" }}>
          <MapContainer
            center={stableCenter}
            zoom={16}
            className="h-full w-full"
            style={{ height: "100%" }}
            zoomControl={false}
            scrollWheelZoom={true}
            doubleClickZoom={false}
          >
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            <MapMoveHandler 
              onMapMove={handleMapMove} 
              onMoveStart={() => setIsMapMoving(true)}
              onMoveEnd={() => setIsMapMoving(false)}
              debounceMs={1500}
            />
            
            {/* Set map ref for external access */}
            <MapRefSetter mapRef={selectionMapRef} />
            
            {/* Map Controls - Zoom and Current Location */}
            <MapControls 
              onCurrentLocation={goToCurrentLocationOnMap}
              isLoadingLocation={loadingLocation}
            />
          </MapContainer>
          
          {/* Center Pin */}
          <CenterPin 
            isPickup={selectingMode === 'pickup'} 
            isMoving={isMapMoving}
            isLoading={geocoding}
          />
          
          {/* Drag instruction */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000] px-4 py-2 bg-black/80 text-white rounded-full text-sm font-medium flex items-center gap-2">
            <Move className="w-4 h-4" />
            Drag map to move pin
          </div>
        </div>

        {/* Confirm Button - stable, doesn't flicker */}
        <div className="p-4 bg-card border-t border-border">
          <Button
            className="w-full h-14 rounded-full font-semibold text-lg"
            onClick={confirmTempLocation}
            disabled={!tempLocation || geocoding || isMapMoving}
            data-testid="confirm-pin-location"
          >
            {geocoding || isMapMoving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                {isMapMoving ? "Stop to select..." : "Finding location..."}
              </>
            ) : (
              <>
                <Check className="w-5 h-5 mr-2" />
                Confirm {selectingMode === 'pickup' ? 'Pickup' : 'Drop-off'}
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Default search view - show both inputs and map preview
  const mapCenter = pickup ? [pickup.lat, pickup.lng] : 
                    drop ? [drop.lat, drop.lng] : 
                    defaultCenter;

  // Handle retry from permission banner
  const handlePermissionRetry = () => {
    setLocationPermissionDenied(false);
    // Try to get current location for pickup by default
    getCurrentLocation('pickup');
  };

  return (
    <div className="h-full flex flex-col">
      {/* Location Permission Banner - shows when permission is denied */}
      {locationPermissionDenied && (
        <div className="p-4 pb-0">
          <LocationPermissionBanner 
            onRetry={handlePermissionRetry}
            compact={false}
          />
        </div>
      )}
      
      {/* Search inputs - ABOVE the map */}
      <div className="p-4 bg-card border-b border-border space-y-3 relative z-[1100]">
        {/* Pickup Search */}
        <div className="relative">
          <div className="flex items-center gap-2">
            <button 
              onClick={startSelectingPickup}
              className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 hover:bg-green-600 transition-colors"
              title="Tap to select pickup on map"
            >
              <span className="text-white text-xs font-bold">P</span>
            </button>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
              <Input
                placeholder="Search pickup location..."
                value={pickup ? (pickup.shortAddress || pickup.address) : pickupSearch}
                onChange={(e) => {
                  if (pickup) {
                    onPickupChange(null);
                    setPickupSearch(e.target.value);
                  } else {
                    setPickupSearch(e.target.value);
                  }
                }}
                onFocus={() => setPickupInputFocused(true)}
                onBlur={() => setTimeout(() => setPickupInputFocused(false), 200)}
                className="pl-10 pr-[115px] h-11"
                data-testid="pickup-search-input"
              />
              {pickup && (
                <button
                  onClick={() => { onPickupChange(null); setPickupSearch(""); }}
                  className="absolute right-[92px] top-1/2 -translate-y-1/2 p-1 z-20"
                >
                  <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                </button>
              )}
              {searchingPickup && (
                <Loader2 className="absolute right-[92px] top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-primary z-20" />
              )}
              <button
                onClick={startSelectingPickup}
                title="Select on map"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-8 z-10 px-2 bg-green-50 hover:bg-green-100 rounded-md transition-colors"
                data-testid="pickup-select-on-map"
              >
                <span className="text-xs font-semibold text-green-600">Select on Map</span>
              </button>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => getCurrentLocation('pickup')}
              disabled={loadingLocation}
              title="Use current location"
              data-testid="pickup-current-location-btn"
              className="h-11 w-11 flex-shrink-0"
            >
              {loadingLocation ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />}
            </Button>
          </div>
          
          {/* Pickup Results Dropdown - HIGH z-index */}
          {pickupResults.length > 0 && (
            <div className="absolute left-10 right-12 mt-1 bg-card border border-border rounded-lg shadow-xl max-h-52 overflow-y-auto z-[1200]">
              {pickupResults.map((result, i) => (
                <button
                  key={i}
                  onMouseDown={() => selectPickupResult(result)}
                  className="w-full text-left px-4 py-3 hover:bg-muted flex items-start gap-3 border-b border-border/50 last:border-0"
                  data-testid={`pickup-result-${i}`}
                >
                  <MapPin className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{result.shortAddress}</p>
                    <p className="text-xs text-muted-foreground truncate">{result.address}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Drop Search */}
        <div className="relative">
          <div className="flex items-center gap-2">
            <button 
              onClick={startSelectingDrop}
              className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0 hover:bg-red-600 transition-colors"
              title="Tap to select drop on map"
            >
              <span className="text-white text-xs font-bold">D</span>
            </button>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
              <Input
                placeholder="Search drop-off location..."
                value={drop ? (drop.shortAddress || drop.address) : dropSearch}
                onChange={(e) => {
                  if (drop) {
                    onDropChange(null);
                    setDropSearch(e.target.value);
                  } else {
                    setDropSearch(e.target.value);
                  }
                }}
                onFocus={() => setDropInputFocused(true)}
                onBlur={() => setTimeout(() => setDropInputFocused(false), 200)}
                className="pl-10 pr-[115px] h-11"
                data-testid="drop-search-input"
              />
              {drop && (
                <button
                  onClick={() => { onDropChange(null); setDropSearch(""); }}
                  className="absolute right-[92px] top-1/2 -translate-y-1/2 p-1 z-20"
                >
                  <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                </button>
              )}
              {searchingDrop && (
                <Loader2 className="absolute right-[92px] top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-primary z-20" />
              )}
              <button
                onClick={startSelectingDrop}
                title="Select on map"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-8 z-10 px-2 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                data-testid="drop-select-on-map"
              >
                <span className="text-xs font-semibold text-red-600">Select on Map</span>
              </button>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => getCurrentLocation('drop')}
              disabled={loadingLocation}
              title="Use current location"
              data-testid="drop-current-location-btn"
              className="h-11 w-11 flex-shrink-0"
            >
              {loadingLocation ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />}
            </Button>
          </div>
          
          {/* Drop Results Dropdown - HIGH z-index */}
          {dropResults.length > 0 && (
            <div className="absolute left-10 right-12 mt-1 bg-card border border-border rounded-lg shadow-xl max-h-52 overflow-y-auto z-[1200]">
              {dropResults.map((result, i) => (
                <button
                  key={i}
                  onMouseDown={() => selectDropResult(result)}
                  className="w-full text-left px-4 py-3 hover:bg-muted flex items-start gap-3 border-b border-border/50 last:border-0"
                  data-testid={`drop-result-${i}`}
                >
                  <Navigation className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{result.shortAddress}</p>
                    <p className="text-xs text-muted-foreground truncate">{result.address}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Distance display */}
        {distance && (
          <div className="text-center py-2 bg-primary/10 rounded-lg border border-primary/20">
            <span className="text-sm text-muted-foreground">Distance: </span>
            <span className="font-bold text-primary text-lg">{distance.toFixed(1)} km</span>
          </div>
        )}
      </div>

      {/* Map Preview - shows selected points */}
      <div className="flex-1 relative" style={{ minHeight: "200px" }}>
        <MapContainer
          center={mapCenter}
          zoom={pickup && drop ? 12 : 13}
          className="h-full w-full"
          style={{ height: "100%" }}
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {/* Set map ref for external access */}
          <MapRefSetter mapRef={previewMapRef} />
          
          {/* Map Controls - Zoom and Current Location */}
          <MapControls 
            onCurrentLocation={goToCurrentLocationOnPreviewMap}
            isLoadingLocation={loadingLocation}
          />
          
          {/* Pan to newly selected location */}
          {panToLocation && (
            <PanToLocation 
              location={panToLocation} 
              zoom={15} 
              onPanComplete={() => setPanToLocation(null)}
            />
          )}
          
          {/* Show both markers only when both are selected */}
          {pickup && drop && (
            <>
              <RouteLine pickup={pickup} drop={drop} />
              <FitBoundsToMarkers pickup={pickup} drop={drop} />
              <Marker 
                position={[pickup.lat, pickup.lng]}
                icon={createMarkerIcon("#22C55E", "P")}
              />
              <Marker 
                position={[drop.lat, drop.lng]}
                icon={createMarkerIcon("#EF4444", "D")}
              />
            </>
          )}
          
          {/* Show single marker if only one is selected */}
          {pickup && !drop && (
            <Marker 
              position={[pickup.lat, pickup.lng]}
              icon={createMarkerIcon("#22C55E", "P")}
            />
          )}
          {drop && !pickup && (
            <Marker 
              position={[drop.lat, drop.lng]}
              icon={createMarkerIcon("#EF4444", "D")}
            />
          )}
        </MapContainer>

        {/* Instruction overlay when nothing selected */}
        {!pickup && !drop && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/10 z-[600]">
            <div className="bg-card px-6 py-4 rounded-xl shadow-lg text-center">
              <MapPin className="w-8 h-8 text-primary mx-auto mb-2" />
              <p className="font-medium text-foreground">Search or tap map icon</p>
              <p className="text-sm text-muted-foreground">to select locations</p>
            </div>
          </div>
        )}
        
        {/* Single location selected indicator */}
        {((pickup && !drop) || (!pickup && drop)) && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[600]">
            <div className="bg-card px-4 py-2 rounded-lg shadow-lg text-center border border-border">
              <p className="text-sm font-medium text-foreground">
                {pickup 
                  ? "Tap the red 'D' icon or pin to select drop-off" 
                  : "Tap the green 'P' icon or pin to select pickup"}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Confirm Button - only when both locations selected */}
      {pickup && drop && (
        <div className="p-4 bg-card border-t border-border">
          <Button
            className="w-full h-14 rounded-full font-semibold text-lg"
            onClick={onConfirm}
            data-testid="confirm-locations-btn"
          >
            <Check className="w-5 h-5 mr-2" />
            Confirm Locations ({distance?.toFixed(1)} km)
          </Button>
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
